import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Droplet, Wrench, Sparkles, Cloud, Shield, Disc, Lightbulb, Zap, Sofa, Home as HomeIcon, Grid3x3 } from 'lucide-react';

const ServicesSectionImproved = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeCategory, setActiveCategory] = useState('all');

  const exteriorServices = [
    {
      icon: Droplet,
      title: 'Lavado Carrocería',
      description: 'Lavado exterior completo',
      image: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxjYXIlMjB3YXNoJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4NDc2MTIzOHww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Wrench,
      title: 'Lavado de motor',
      description: 'Limpieza profesional del motor',
      image: 'https://images.pexels.com/photos/29504462/pexels-photo-29504462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Sparkles,
      title: 'Descontaminación de pintura',
      description: 'Eliminación de impurezas',
      image: 'https://images.pexels.com/photos/37809561/pexels-photo-37809561.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Cloud,
      title: 'Eliminación de lluvia ácida',
      description: 'En pintura y vidrios',
      image: 'https://images.unsplash.com/photo-1608506375591-b90e1f955e4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjYXIlMjB3YXNoJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4NDc2MTIzOHww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Shield,
      title: 'Encerado',
      description: 'Protección y brillo con cera premium',
      image: 'https://images.unsplash.com/photo-1620584899131-a5ff5f8fbb03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxjYXIlMjBwb2xpc2hpbmd8ZW58MHx8fHwxNzg0NzYxMjQ3fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Disc,
      title: 'Desmanchado de mordazas',
      description: 'Eliminación de manchas difíciles',
      image: 'https://images.pexels.com/photos/17623850/pexels-photo-17623850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Lightbulb,
      title: 'Pulida de faros',
      description: 'Restauración de faros opacos',
      image: 'https://images.unsplash.com/photo-1633014041037-f5446fb4ce99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxjYXIlMjBkZXRhaWxpbmd8ZW58MHx8fHwxNzg0NjYwNTExfDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: Zap,
      title: 'Pulido',
      description: 'Pulido general de pintura',
      image: 'https://images.pexels.com/photos/37809565/pexels-photo-37809565.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const interiorServices = [
    {
      icon: Sofa,
      title: 'Limpieza de tapicería',
      description: 'Limpieza profunda interior',
      image: 'https://images.unsplash.com/photo-1608506375591-b90e1f955e4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjYXIlMjB3YXNoJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4NDc2MTIzOHww&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: HomeIcon,
      title: 'Limpieza techo',
      description: 'Limpieza especializada del techo',
      image: 'https://images.pexels.com/photos/29504462/pexels-photo-29504462.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: Grid3x3,
      title: 'Limpieza de alfombras',
      description: 'Lavado profundo de alfombras',
      image: 'https://images.pexels.com/photos/17623850/pexels-photo-17623850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const allServices = [...exteriorServices, ...interiorServices];

  const getFilteredServices = () => {
    if (activeCategory === 'exterior') return exteriorServices;
    if (activeCategory === 'interior') return interiorServices;
    return allServices;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="services" className="bg-[#0F1D33] py-24 md:py-32 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #60A5FA 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        {/* Section Number */}
        <motion.div variants={itemVariants} className="mb-8">
          <span className="text-[#60A5FA] text-lg font-mono">02</span>
        </motion.div>

        {/* Section Title */}
        <motion.h2 variants={itemVariants} className="text-5xl md:text-6xl font-bold text-white mb-6">
          Nuestros <span className="text-[#60A5FA]">Servicios</span>
        </motion.h2>

        <motion.p variants={itemVariants} className="text-white/70 text-lg mb-12 max-w-2xl">
          Servicios profesionales de detailing automotriz para tu vehículo
        </motion.p>

        {/* Category Filter */}
        <motion.div variants={itemVariants} className="flex flex-wrap gap-4 mb-12">
          {[
            { id: 'all', label: 'Todos los servicios' },
            { id: 'exterior', label: 'Exterior' },
            { id: 'interior', label: 'Interior' },
          ].map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-white shadow-lg shadow-[#60A5FA]/30'
                  : 'bg-[#0A1628]/50 text-white/70 border border-white/10 hover:border-[#60A5FA]/50 hover:text-white'
              }`}
            >
              {category.label}
            </button>
          ))}
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {getFilteredServices().map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                layout
                key={service.title}
                variants={itemVariants}
                whileHover={{ y: -12, scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A1628] to-[#0F1D33] border border-white/10 hover:border-[#60A5FA]/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-[#60A5FA]/20"
              >
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-15 transition-opacity duration-700">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#60A5FA]/0 to-[#3B82F6]/0 group-hover:from-[#60A5FA]/10 group-hover:to-[#3B82F6]/10 transition-all duration-500" />

                {/* Content */}
                <div className="relative p-8 z-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#60A5FA]/20 to-[#3B82F6]/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                    <Icon className="w-10 h-10 text-[#60A5FA]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#60A5FA] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">{service.description}</p>
                </div>

                {/* Bottom Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#60A5FA] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ServicesSectionImproved;
